import React, { useState, useEffect } from "react";
import Modal from "../../../components/modal/Modal";
import { toast } from "react-toastify";
import { X, Upload, FileText, User, Building } from "lucide-react";
import { useS3Upload } from "../../../utils/s3Client";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  createEquipmentHistory,
  updateEquipmentHistory,
  EquipmentHistory,
  Attachment,
  getAllEquipmentManagementData,
  getAllSuppliers,
} from "../../../service/api";
import { v4 as uuidv4 } from "uuid";

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: EquipmentHistory;
}

interface FormData {
  eventId: string;
  equipmentId: string;
  equipmentName: string;
  eventType: EquipmentHistory["eventType"];
  eventDate: string;
  notes: string;
  cost: string;
  performedBy: string;
  vendor: string;
  attachments: File[]; // New files to upload
  existingAttachments: Attachment[]; // Existing attachments from database
}

interface FormErrors {
  eventId?: string;
  equipmentId?: string;
  equipmentName?: string;
  eventType?: string;
  eventDate?: string;
  notes?: string;
  cost?: string;
  performedBy?: string;
  vendor?: string;
}

interface EquipmentOption {
  id: string;
  name: string;
}

interface Vendors {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
}

const AddEventModal: React.FC<AddEventModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const [formData, setFormData] = useState<FormData>({
    eventId: initialData?.eventId || `EV-${uuidv4().slice(0, 8).toUpperCase()}`,
    equipmentId: initialData?.equipmentId || "",
    equipmentName: initialData?.equipmentName || "",
    eventType: initialData?.eventType || "Service",
    eventDate: initialData?.eventDate || new Date().toISOString().split("T")[0],
    notes: initialData?.notes || "",
    cost: initialData?.cost?.toString() || "",
    performedBy: initialData?.performedBy || "",
    vendor: initialData?.vendor || "",
    attachments: [], // New files
    existingAttachments: initialData?.attachments || [], // Existing attachments
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>(
    initialData?.region || ""
  );
  const [selectedPremises, setSelectedPremises] = useState<string>(
    initialData?.premises || ""
  );
  const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOption[]>(
    []
  );
  const [vendors, setVendors] = useState<Vendors[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const eventTypeOptions = [
    "Purchase",
    "Service",
    "Repair",
    "Calibration",
    "Transfer",
    "Decommission",
    "Inspection",
    "Incident",
  ];

  // Fetch equipment data
  useEffect(() => {
    setLoadingVendors(true);
    const fetchVendors = async () => {
      try {
        const response = await getAllSuppliers();
        setVendors(response.data.data || []);
      } catch (error) {
        toast.error("Failed to fetch Vendors data");
      } finally {
        setLoadingVendors(false);
      }
    };
    const fetchEquipment = async () => {
      try {
        const response = await getAllEquipmentManagementData();
        if (response.data && Array.isArray(response.data.results)) {
          const equipment = response.data.results.map((item: any) => ({
            id: item.equipmentId,
            name: item.equipmentName,
          }));
          setEquipmentOptions(equipment);
        } else {
          setEquipmentOptions([]);
          toast.error("Failed to fetch equipment data");
        }
      } catch (error: any) {
        console.error("Error fetching equipment data:", error);
        toast.error("Failed to fetch equipment data");
        setEquipmentOptions([]);
      }
    };
    fetchVendors();
    fetchEquipment();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        eventId:
          initialData.eventId || `EV-${uuidv4().slice(0, 8).toUpperCase()}`,
        equipmentId: initialData.equipmentId,
        equipmentName: initialData.equipmentName,
        eventType: initialData.eventType,
        eventDate: initialData.eventDate.split("T")[0],
        notes: initialData.notes,
        cost: initialData.cost?.toString() || "",
        performedBy: initialData.performedBy || "",
        vendor: initialData.vendor || "",
        attachments: [], // Reset new files
        existingAttachments: initialData.attachments || [], // Keep existing attachments
      });
      setSelectedRegion(initialData.region || "");
      setSelectedPremises(initialData.premises || "");
    }
  }, [initialData]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEquipmentSelect = (id: string) => {
    const selectedEquipment = equipmentOptions.find((eq) => eq.id === id);
    if (selectedEquipment) {
      setFormData((prev) => ({
        ...prev,
        equipmentId: selectedEquipment.id,
        equipmentName: selectedEquipment.name,
      }));
      if (errors.equipmentId) {
        setErrors((prev) => ({ ...prev, equipmentId: undefined }));
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles],
      }));
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveExistingFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      existingAttachments: prev.existingAttachments.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.eventId) newErrors.eventId = "Event ID is required";
    if (!formData.equipmentId) newErrors.equipmentId = "Equipment is required";
    if (!formData.eventType) newErrors.eventType = "Event type is required";
    if (!formData.eventDate) newErrors.eventDate = "Event date is required";
    if (!formData.notes) newErrors.notes = "Notes are required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      let attachmentUrls: string[] = [];

      // Upload new files
      if (formData.attachments.length > 0) {
        for (const file of formData.attachments) {
          const url = await uploadFileToS3(file);
          if (url) {
            attachmentUrls.push(url);
          } else {
            throw new Error("Failed to upload attachment");
          }
        }
      }

      // Combine existing attachments (with their URLs) and new attachments
      const allAttachments = [
        ...formData.existingAttachments.map((att) => ({ url: att.url })),
        ...attachmentUrls.map((url) => ({ url })),
      ];

      const payload: Partial<EquipmentHistory> = {
        eventId: formData.eventId,
        equipmentId: formData.equipmentId,
        equipmentName: formData.equipmentName,
        eventType: formData.eventType,
        eventDate: formData.eventDate,
        notes: formData.notes,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        performedBy: formData.performedBy || undefined,
        vendor: formData.vendor || undefined,
        region: selectedRegion || undefined,
        premises: selectedPremises || undefined,
        attachments: allAttachments.length > 0 ? allAttachments : undefined,
        createdBy: initialData ? undefined : "Current User",
      };

      if (initialData) {
        if (!initialData._id) {
          throw new Error("Event ID is missing for update.");
        }
        await updateEquipmentHistory(initialData._id, payload);
        toast.success("Event updated successfully");
      } else {
        await createEquipmentHistory(payload);
        toast.success("Event added successfully");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error submitting event:", error);
      toast.error("Failed to save event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      eventId: `EV-${uuidv4().slice(0, 8).toUpperCase()}`,
      equipmentId: "",
      equipmentName: "",
      eventType: "Service",
      eventDate: new Date().toISOString().split("T")[0],
      notes: "",
      cost: "",
      performedBy: "",
      vendor: "",
      attachments: [],
      existingAttachments: [],
    });
    setErrors({});
    setSelectedRegion("");
    setSelectedPremises("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      modalClass="bg-white w-full max-w-5xl rounded-xl shadow-lg max-h-[80vh] flex flex-col relative"
      closeButton={false}
    >
      <form
        id="addEventModal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-6 pb-24 overflow-auto"
      >
        <div className="flex justify-between items-center px-4 py-3">
          <h2 className="text-2xl font-semibold text-gray-800">
            {initialData ? "Edit Event" : "Add New Event"}
          </h2>
          {/* <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-3">
          {/* Event ID (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.eventId}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
            />
            {errors.eventId && (
              <p className="text-red-500 text-xs mt-1">{errors.eventId}</p>
            )}
          </div>

          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Equipment <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.equipmentId}
              onChange={(e) => handleEquipmentSelect(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] ${
                errors.equipmentId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Equipment</option>
              {equipmentOptions.map((equipment) => (
                <option key={equipment.id} value={equipment.id}>
                  {equipment.name} ({equipment.id})
                </option>
              ))}
            </select>
            {errors.equipmentId && (
              <p className="text-red-500 text-xs mt-1">{errors.equipmentId}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.eventType}
              onChange={(e) => handleInputChange("eventType", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] ${
                errors.eventType ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Event Type</option>
              {eventTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            {errors.eventType && (
              <p className="text-red-500 text-xs mt-1">{errors.eventType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.eventDate}
              onChange={(e) => handleInputChange("eventDate", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] ${
                errors.eventDate ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.eventDate && (
              <p className="text-red-500 text-xs mt-1">{errors.eventDate}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={(e) => handleInputChange("cost", e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Performed By
            </label>
            <div className="relative">
              <User className="absolute right-3 top-2.5 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={formData.performedBy}
                onChange={(e) =>
                  handleInputChange("performedBy", e.target.value)
                }
                placeholder="Name of person or team"
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor/Supplier
            </label>
            <div className="relative">
              {/*<Building className="absolute right-3 top-2.5 text-gray-400 w-5 h-5" />
               <input
                type="text"
                value={formData.vendor}
                onChange={(e) => handleInputChange("vendor", e.target.value)}
                placeholder="Vendor name"
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
              /> */}
              <select
                name="vendor"
                value={formData.vendor}
                onChange={(e) => handleInputChange("vendor", e.target.value)}
                disabled={loadingVendors}
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6]"
              >
                <option value="">Select Vendor</option>
                {loadingVendors ? (
                  <option value="" disabled>
                    Loading vendors...
                  </option>
                ) : (
                  vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.name}>
                      {vendor.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
        {/* Region and Premises Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Region
            </label>
            <Dropdown
              selectedValue={selectedRegion}
              onChange={setSelectedRegion}
              type="region"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Premises
            </label>
            <Dropdown
              selectedValue={selectedPremises}
              onChange={setSelectedPremises}
              type="premises"
              selectedRegion={selectedRegion}
            />
          </div>
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] ${
              errors.notes ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Describe the event, including any important details, observations, or actions taken..."
          />
          {errors.notes && (
            <p className="text-red-500 text-xs mt-1">{errors.notes}</p>
          )}
        </div>

        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachments
          </label>

          {/* Show existing attachments in edit mode */}
          {formData.existingAttachments.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Existing Files:
              </p>
              {formData.existingAttachments.map((attachment, index) => {
                const filename =
                  attachment.filename ||
                  attachment.name ||
                  attachment.originalName ||
                  attachment.url?.split("/").pop() ||
                  `File ${index + 1}`;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 p-2 rounded mb-2"
                  >
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-700 truncate max-w-xs">
                        {filename}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="border-2 border-dashed border-gray-300 rounded-md p-4">
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center cursor-pointer p-4 text-center"
            >
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600">
                Drag & drop files here or click to browse
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (Max 5MB each)
              </p>
            </label>
          </div>

          {formData.attachments.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">
                New files to upload:
              </p>
              {formData.attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-2 rounded"
                >
                  <div className="flex items-center">
                    <FileText className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-700 truncate max-w-xs">
                      {file.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveNewFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading files...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-[#0fa9a6] h-2 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </form>
      <div className="flex justify-end gap-4 p-4 bg-[#5A7192] rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={handleClose}
          className="px-6 py-2 border bg-[#8497B0] rounded-full text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="addEventModal"
          disabled={isSubmitting || uploading}
          className="px-6 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-[#0e9693] focus:outline-none focus:ring-2 focus:ring-[#0fa9a6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {initialData ? "Updating Event..." : "Adding Event..."}
            </>
          ) : initialData ? (
            "Update Event"
          ) : (
            "Add Event"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default AddEventModal;
